// src/data/presentedToCheck.js
//
// Record 610 (ruling 259/262). WHO THE CLAIM IS PRESENTED TO: THE AGREEMENT, THE GC'S DIRECTORY AND THE LC'S
// DIRECTORY CHECK EACH OTHER. Ruling 262 (data\YCP_RULINGS_2026-09-29.md, "## 262."), Bob 2026-09-29: "allow the
// controlling agreement language to cross reference the gcs directory and the lcs directory (both should have their
// own to cross reference against each other rather than one dictating to the other without knowing there is an error
// or worse."
//
// THREE SOURCES, NONE OVERRIDING ANOTHER:
//   the AGREEMENT  - the title that receives a claim, read only: claimRequirements.js presentmentOfficer, passed in
//                    exactly as that file holds it and never reworded here (PD-16);
//   the GC's DIRECTORY - directories/{committee}, written by that committee's General Chairperson;
//   the LC's DIRECTORY - directories/{committee}/locals/{scopeKey}, written by the Local Chairperson of that seat.
// Each directory names, per agreement, the carrier officer: name, title, address and delivery method.
//
// WHAT THIS MODULE DOES: compares them and SAYS SO. It decides nothing, writes nothing, and never blocks (ruling 262:
// "Never a block: an unresolved presentment is on the not-ready list; the claim still saves and files"). The filer
// makes the final choice on the claim; choiceRecord() is what is recorded with it - the choice and what each source
// said at that moment. Neither directory is ever changed by a claim.
//
// Pure: no React, no Firestore, no imports - so scripts\test-presented-to-check.mjs runs it with plain node.
// Withdrawable by number: RULING_262_DIRECTORIES false -> crossCheck() reports 'off' and adds nothing to the form.

export const RULING_262_DIRECTORIES = true;
export const CHECK_VERSION = '262-v1';
export const MAX_LINE = 120;
export const MAX_ADDRESS_LINES = 8;
export const MAX_NOTE = 200;

const s = (v) => (v == null ? '' : String(v).replace(/[\r\n]+/g, ' ').trim());
const lines = (v) => (Array.isArray(v) ? v : (typeof v === 'string' ? v.split(/\r?\n/) : []))
  .map((x) => s(x).slice(0, MAX_LINE)).filter(Boolean).slice(0, MAX_ADDRESS_LINES);

// ------------------------------------------------------------------------------------------------ one entry
// An entry reduced to its stored shape. Unknown keys are dropped; provenance is carried through when present.
export function normalizeEntry(e) {
  const i = e || {};
  const out = {
    name: s(i.name).slice(0, MAX_LINE),
    title: s(i.title).slice(0, MAX_LINE),
    addressLines: lines(i.addressLines),
    method: s(i.method).slice(0, 40),
    note: s(i.note).slice(0, MAX_NOTE),
  };
  if (s(i.agreement)) out.agreement = s(i.agreement);
  if (s(i.recordedBy)) out.recordedBy = s(i.recordedBy);
  if (s(i.recordedByName)) out.recordedByName = s(i.recordedByName);
  if (s(i.recordedAt)) out.recordedAt = s(i.recordedAt);
  return out;
}

// An entry counts only when it names a person AND a title - a directory row with neither tells the filer nothing.
export function hasEntry(e) { return !!(e && s(e.name) && s(e.title)); }

// The words that go on the claim's "Presented to" line: "Name, Title", exactly as the directory holds them.
export function entryLine(e) {
  if (!e) return '';
  return [s(e.name), s(e.title)].filter(Boolean).join(', ');
}

// methodKeys: the presentment methods the caller offers (claimPresentment.js METHOD_KEYS). Optional so the module
// stays import-free; when given, a method outside the list is refused.
export function validateEntry(input, methodKeys) {
  const value = normalizeEntry(input);
  const errors = [];
  if (!value.name) errors.push('Name the officer who receives claims.');
  if (!value.title) errors.push('Give his title.');
  if (value.method && Array.isArray(methodKeys) && methodKeys.indexOf(value.method) === -1) errors.push('"' + value.method + '" is not one of the delivery methods.');
  return { ok: errors.length === 0, errors, value };
}

// The entry a directory document holds for one agreement (by slug - serviceSettings.js agreementSlug), or null.
export function entryFor(dirDoc, slug) {
  const a = dirDoc && dirDoc.agreements && slug ? dirDoc.agreements[slug] : null;
  return a && hasEntry(a) ? normalizeEntry(a) : null;
}

// The patch an officer writes to HIS directory: one agreement's entry set (or removed with input === null), every
// other agreement's entry kept as it is. Returns { ok, errors, agreements } - never a partial map.
export function directoryEntryPatch(current, slug, agreementName, input, user, at, methodKeys) {
  const errors = [];
  if (!s(slug)) errors.push('No agreement.');
  if (!user || !s(user.uid)) errors.push('No signed-in account.');
  const agreements = { ...((current && current.agreements) || {}) };
  if (input === null) {
    if (!errors.length) delete agreements[slug];
    return errors.length ? { ok: false, errors, agreements: null } : { ok: true, errors: [], agreements };
  }
  const v = validateEntry(input, methodKeys);
  errors.push(...v.errors);
  if (errors.length) return { ok: false, errors, agreements: null };
  agreements[slug] = { ...v.value, agreement: s(agreementName), recordedBy: s(user.uid),
    recordedByName: s(user.name || user.email) || 'Unknown', recordedAt: at || new Date().toISOString() };
  return { ok: true, errors: [], agreements };
}

// ------------------------------------------------------------------------------------------------ comparing
const norm = (v) => s(v).toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
const normAddr = (e) => norm((e && e.addressLines ? e.addressLines : []).join(' '));

// Which parts of two entries differ: 'name', 'title', 'address', 'method'. A part one side left blank is not a
// difference in that part - an empty field is silence, not a contrary statement.
export function entryDifferences(a, b) {
  const out = [];
  if (!a || !b) return out;
  if (norm(a.name) !== norm(b.name)) out.push('name');
  if (norm(a.title) !== norm(b.title)) out.push('title');
  if (normAddr(a) && normAddr(b) && normAddr(a) !== normAddr(b)) out.push('address');
  if (s(a.method) && s(b.method) && s(a.method) !== s(b.method)) out.push('method');
  return out;
}

// Initials of each run of Capitalised words in the agreement's text: "Highest Designated Officer" -> HDO.
function initialisms(text) {
  const out = [];
  const runs = String(text || '').match(/(?:\b[A-Z][a-z]+\b[ \t]*){2,}/g) || [];
  runs.forEach((r) => out.push(r.trim().split(/\s+/).map((w) => w[0]).join('')));
  return out;
}
// The agreement designates by AUTHORITY or RELATION, not only by title: any titled officer may be the one it means.
const GENERIC = /authori[sz]ed|designated officer|other designated|immediate supervisor/i;

// Does the agreement's own designation name this title?
//   'named'          - the title is in the designation's words (or is an initialism of a title there: HDO)
//   'generic'        - not named, but the designation is by authority or relation, so it cannot be ruled out
//   'not_named'      - the designation names titles, and this is not one of them (ruling 262: flagged)
//   'no_designation' - the agreement profile holds no designation to compare against
export function titleVerdict(title, designation) {
  const d = s(designation);
  if (!d) return 'no_designation';
  const t = norm(title);
  if (!t) return 'not_named';
  if (norm(d).indexOf(t) !== -1) return 'named';
  const compact = s(title).replace(/[^A-Za-z]/g, '');
  if (compact.length >= 2 && compact.length <= 6 && compact === compact.toUpperCase()
      && initialisms(d).indexOf(compact) !== -1) return 'named';
  return GENERIC.test(d) ? 'generic' : 'not_named';
}

// Does the filer's own answer name this officer? His surname (last word of the name) and the title, or the whole
// "Name, Title" line, appearing in what he wrote.
export function answerNames(answer, e) {
  const a = norm(answer);
  if (!a || !hasEntry(e)) return false;
  if (a.indexOf(norm(entryLine(e))) !== -1) return true;
  const words = norm(e.name).split(' ').filter(Boolean);
  const surname = words[words.length - 1] || '';
  return !!surname && (' ' + a + ' ').indexOf(' ' + surname + ' ') !== -1;
}

const WHO = { gc: 'the General Chairperson’s directory', lc: 'the Local Chairperson’s directory' };

// THE CHECK. input: { answer, designation, gc, lc, recorded }
//   answer       the claim's presentedTo (the filer's answer)
//   designation  claimRequirements.js presentmentOfficer for the claim's agreement, verbatim
//   gc, lc       entryFor() of each directory for that agreement (null when none)
//   recorded     the claim's presentedToCheck (choiceRecord output), when a choice was recorded
// Returns { status, suggestion, suggestionLine, candidates, answerState, titleFlags, notices, resolved,
//           formMissing, formChecks }.
//   status: 'off' | 'no_directory' | 'one_source' | 'agree' | 'differ'
export function crossCheck(input) {
  const i = input || {};
  const empty = { status: 'off', suggestion: null, suggestionLine: '', candidates: [], answerState: 'unchecked',
    titleFlags: [], notices: [], resolved: true, formMissing: '', formChecks: [] };
  if (!RULING_262_DIRECTORIES) return empty;
  const answer = s(i.answer);
  const designation = s(i.designation);
  const gc = hasEntry(i.gc) ? normalizeEntry(i.gc) : null;
  const lc = hasEntry(i.lc) ? normalizeEntry(i.lc) : null;
  const notices = [];
  const formChecks = [];
  const candidates = [];
  if (gc) candidates.push({ who: 'gc', entry: gc, line: entryLine(gc) });
  if (lc) candidates.push({ who: 'lc', entry: lc, line: entryLine(lc) });

  let status, suggestion = null, differences = [];
  if (!gc && !lc) {
    status = 'no_directory';
    notices.push({ key: 'no_directory', level: 'info', text: 'Neither the General Chairperson’s nor the Local Chairperson’s directory names an officer for this agreement yet. The claim says who it is presented to in the filer’s own words.' });
  } else if (!gc || !lc) {
    status = 'one_source';
    suggestion = gc || lc;
    notices.push({ key: 'one_source', level: 'info', text: 'Checked against one source only: ' + WHO[gc ? 'gc' : 'lc'] + ' names ' + entryLine(suggestion) + '. ' + (gc ? 'The Local Chairperson’s directory' : 'The General Chairperson’s directory') + ' names no one for this agreement.' });
  } else {
    differences = entryDifferences(gc, lc);
    if (!differences.length) {
      status = 'agree';
      suggestion = gc;
      notices.push({ key: 'agree', level: 'ok', text: 'Both directories agree: ' + entryLine(gc) + '.' });
    } else {
      status = 'differ';
      notices.push({ key: 'differ', level: 'warn', text: 'The two directories differ (' + differences.join(', ') + '). ' + WHO.gc + ' names ' + entryLine(gc) + '; ' + WHO.lc + ' names ' + entryLine(lc) + '. Both are shown; neither record is changed. The General Chairperson resolves the difference in the directories; on this claim, the filer chooses.' });
    }
  }

  // A directory title the agreement does not name (ruling 262: "flagged (wrong presentment can defeat a claim on time)").
  const titleFlags = [];
  candidates.forEach((c) => {
    const v = titleVerdict(c.entry.title, designation);
    titleFlags.push({ who: c.who, title: c.entry.title, verdict: v });
    if (v === 'not_named') {
      const t = 'The title "' + c.entry.title + '" in ' + WHO[c.who] + ' is not one the agreement names. The agreement profile names: ' + designation + '. Presentment to the wrong officer can defeat a claim on time.';
      notices.push({ key: 'title_' + c.who, level: 'warn', text: t });
      formChecks.push(t);
    } else if (v === 'generic') {
      notices.push({ key: 'title_' + c.who, level: 'info', text: 'The agreement names the receiving officer by authority, not by title alone (' + designation + '). Confirm ' + entryLine(c.entry) + ' is that officer.' });
    }
  });

  // The filer's answer against the directories.
  let answerState = 'unchecked';
  if (!answer) answerState = 'blank';
  else if (candidates.length) {
    answerState = candidates.some((c) => answerNames(answer, c.entry)) ? 'matches' : 'differs';
    if (answerState === 'differs') {
      const t = 'The claim names ' + answer + '; ' + candidates.map((c) => WHO[c.who] + ' names ' + c.line).join('; ') + '. Confirm who receives it before it is served.';
      notices.push({ key: 'answer_differs', level: 'warn', text: t });
      formChecks.push(t);
    }
  }
  if (status === 'one_source' && answer) formChecks.push('Who the claim is presented to was checked against one source only.');

  // Resolved: nothing conflicts, or the filer recorded a choice that is still the claim's answer.
  const rec = i.recorded && s(i.recorded.chosen) ? i.recorded : null;
  const choiceStands = !!rec && norm(rec.chosen) === norm(answer) && !!answer;
  const resolved = status !== 'differ' || choiceStands;
  // Deliberately avoids the words "presented to": claimCompletion.js marks its presentedTo row blank on /presented to/.
  const formMissing = resolved ? '' : 'a choice between the two directories for this claim’s presentment (the General and Local Chairpersons’ directories differ)';
  if (status === 'differ' && choiceStands) {
    notices.push({ key: 'chosen', level: 'ok', text: 'Chosen on this claim by ' + (s(rec.recordedByName) || 'the filer') + (s(rec.recordedAt) ? ' on ' + s(rec.recordedAt).slice(0, 10) : '') + ': ' + s(rec.chosen) + '.' });
  }

  return { status, suggestion, suggestionLine: entryLine(suggestion), candidates, differences, answerState, titleFlags,
    notices, resolved, formMissing, formChecks };
}

// What is recorded on the claim when the filer chooses (ruling 262: "the choice and what each source said then are
// recorded on the claim"). Returns { ok, errors, patch } - patch is { presentedTo, presentedToCheck }.
export function choiceRecord(result, chosen, input, user, at) {
  const i = input || {};
  const errors = [];
  const c = s(chosen);
  if (!c) errors.push('Say who the claim is presented to.');
  if (!user || !s(user.uid)) errors.push('No signed-in account.');
  if (errors.length) return { ok: false, errors, patch: null };
  const r = result || {};
  const snap = (e) => (hasEntry(e) ? normalizeEntry(e) : null);
  return {
    ok: true, errors: [],
    patch: {
      presentedTo: c,
      presentedToCheck: {
        version: CHECK_VERSION,
        chosen: c,
        status: s(r.status) || 'unchecked',
        answerBefore: s(i.answer) || null,
        agreement: s(i.agreement) || null,
        designation: s(i.designation) || null,
        gc: snap(i.gc),
        lc: snap(i.lc),
        recordedBy: s(user.uid),
        recordedByName: s(user.name || user.email) || 'Unknown',
        recordedAt: at || new Date().toISOString(),
      },
    },
  };
}

// FOR THE DESKS (ruling 262: "GC and LC differ - both shown to the filer, both officers notified"). Each officer's
// directory panel lists where the other directory differs from his, and every title the agreement does not name.
//   gcDoc        directories/{committee}
//   localDocs    [{ id: scopeKey, ...directories/{committee}/locals/{scopeKey} }]
//   designations { [slug]: presentmentOfficer text }
// Returns [{ slug, agreement, scopeKey, kind: 'differ' | 'title', gc, lc, differences, who, title }]
export function directoryConflicts(gcDoc, localDocs, designations) {
  const out = [];
  const d = designations || {};
  const gcA = (gcDoc && gcDoc.agreements) || {};
  const locals = Array.isArray(localDocs) ? localDocs : [];
  Object.keys(gcA).forEach((slug) => {
    const g = entryFor(gcDoc, slug);
    if (!g) return;
    if (titleVerdict(g.title, d[slug]) === 'not_named') out.push({ slug, agreement: g.agreement || slug, scopeKey: '', kind: 'title', who: 'gc', title: g.title, gc: g, lc: null, differences: [] });
  });
  locals.forEach((ld) => {
    const la = (ld && ld.agreements) || {};
    const scopeKey = s(ld && (ld.scopeKey || ld.id));
    Object.keys(la).forEach((slug) => {
      const l = entryFor(ld, slug);
      if (!l) return;
      const g = entryFor(gcDoc, slug);
      if (g) {
        const diff = entryDifferences(g, l);
        if (diff.length) out.push({ slug, agreement: g.agreement || l.agreement || slug, scopeKey, kind: 'differ', who: 'both', title: '', gc: g, lc: l, differences: diff });
      }
      if (titleVerdict(l.title, d[slug]) === 'not_named') out.push({ slug, agreement: l.agreement || slug, scopeKey, kind: 'title', who: 'lc', title: l.title, gc: g || null, lc: l, differences: [] });
    });
  });
  return out;
}

// The office address a seat holder enters for his letters (ruling 259: "the office address entered once by the seat
// holder"), cleaned to stored lines.
export function officeAddressLines(input) { return lines(input); }
